import { Component, Input, OnInit, inject } from '@angular/core';
import { ProcessKey, ReorganizeProcessOption, ReorganizeProcessResult } from '@app/models/production/finishing-route';
import { AuthService, BaseModalComponent } from '@card-software/library';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { ProcessesProxyService } from '@app/proxies/item/processes.proxy.service';
import { FinishingRoles } from '@app/models/roles/roles-group';
import { ROLLING_PROCESS_ID } from '@app/utilities/finishing-route.util';

@Component({
  selector: 'app-reorganize-process-modal',
  templateUrl: './reorganize-process-modal.component.html',
  styleUrl: './reorganize-process-modal.component.scss',
})
export class ReorganizeProcessModalComponent extends BaseModalComponent<ReorganizeProcessResult | null> implements OnInit {
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly processesProxyService = inject(ProcessesProxyService);

  @Input() processes: ProcessKey[] = [];

  completedProcesses: ProcessKey[] = [];
  inProgressProcesses: ProcessKey[] = [];
  availableProcesses: ReorganizeProcessOption[] = [];
  pendingProcessesList: ReorganizeProcessOption[] = [];
  selectedProcessToAdd: string | null = null;
  isRouteCompleted = false;
  isRollingAdmin = AuthService.hasRole(FinishingRoles.FinishingRouteRollingAdmin);

  private allReorganizeProcessOptiones: ReorganizeProcessOption[] = [];
  processTypeReference = '67aab7bd6b1dfb85a9311f22'; // Finishing Process Type Reference

  ngOnInit(): void {
    this.loadFinishingProcesses();
  }

  private loadFinishingProcesses(): void {
    this.processesProxyService.searchProcesses({ showInFabricGeneralProcessRoute: true }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const finishingProcesses = response.data.filter(
            (p) =>
              p.processTypeReference === this.processTypeReference ||
              (this.isRollingAdmin && p.processId.toString() === ROLLING_PROCESS_ID),
          );

          this.allReorganizeProcessOptiones = finishingProcesses.map((p) => ({
            id: p.processId.toString(),
            name: this.localeHelper.getLocalizedValue(p.name) || p.name.en,
          }));

          this.initializeProcessLists();
        }
      },
      error: (error) => {
        console.error('Error loading processes:', error);
        this.initializeProcessLists();
      },
    });
  }

  private initializeProcessLists(): void {
    this.completedProcesses = this.processes.filter((p) => p.endTime != null);
    this.inProgressProcesses = this.processes.filter((p) => p.startTime != null && p.endTime == null);

    const pendingProcesses = this.processes.filter((p) => p.startTime == null && p.endTime == null);

    // Check if route is fully completed (all processes have end times)
    this.isRouteCompleted = this.processes.length > 0 && this.processes.every((p) => p.endTime != null);

    this.pendingProcessesList = pendingProcesses.map((p) => {
      const found = this.allReorganizeProcessOptiones.find((ap) => ap.id.toLowerCase() === p.processId.toLowerCase());

      if (found) {
        return found;
      }

      const name = this.localeHelper.getLocalizedValue(p.processName) || p.processId;
      return { id: p.processId, name: name };
    });

    this.updateReorganizeProcessOptiones();
  }

  updateReorganizeProcessOptiones(): void {
    const pendingIds = new Set(this.pendingProcessesList.map((p) => p.id));

    const baseList = this.isRollingAdmin
      ? this.allReorganizeProcessOptiones
      : this.allReorganizeProcessOptiones.filter((p) => p.id !== ROLLING_PROCESS_ID);

    this.availableProcesses = baseList.filter((p) => !pendingIds.has(p.id));
  }

  getProcessName(process: ProcessKey): string {
    const found = this.allReorganizeProcessOptiones.find((ap) => ap.id.toLowerCase() === process.processId.toLowerCase());

    if (found) {
      return found.name;
    }

    return this.localeHelper.getLocalizedValue(process.processName) || process.processId;
  }

  onProcessSelected(): void {
    if (!this.selectedProcessToAdd) return;

    const selectedProcess = this.allReorganizeProcessOptiones.find((p) => p.id === this.selectedProcessToAdd);

    if (selectedProcess) {
      this.pendingProcessesList.push(selectedProcess);
      this.updateReorganizeProcessOptiones();

      // Clear selection using setTimeout to ensure Angular change detection completes
      setTimeout(() => {
        this.selectedProcessToAdd = null;
      }, 0);
    }
  }

  onReorder(): void {
    // Reorder is handled by p-orderList binding; no additional action needed
  }

  onRemoveProcess(index: number): void {
    this.pendingProcessesList.splice(index, 1);
    this.updateReorganizeProcessOptiones();
  }

  onSave(): void {
    const pendingProcesses: ProcessKey[] = this.pendingProcessesList.map((ap) => ({
      lineId: ap.id,
      processId: ap.id,
      dateTimeAdded: new Date(),
      startTime: null,
      endTime: null,
    }));

    const result: ReorganizeProcessResult = {
      pendingProcesses: pendingProcesses,
    };

    this.close({ action: 'save', data: result });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }
}
